import { observer } from "mobx-react-lite";
import styled from "@emotion/styled";
import { useEffect, useMemo, useState } from "react";
import {
  MDSButton,
  MDSButtonVariant,
  MDSButtonSlotName,
  MDSIcon,
  MDSIconColor,
  MDSIconKind,
  MDSMenuContentItemKind,
  MDSMenuContentList,
  MDSMenuDropdownSlotName,
  MDSIconButton,
  MDSIconButtonSize,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
  MDSShortcutKey,
  MDSShortcutKeyCharacter,
  MDSBanner,
  MDSBannerKind,
  MDSBannerSlotName,
  MDSToggle,
} from "@/mds";
import { MDSTextField } from "@/mds/components/forms/text-field/text-field";
import { MDSActionMenuDropdown } from "@/mds/components/menu/action-menu/action-menu-dropdown";
import { MDSTooltip, MDSTooltipPlacement } from "@/mds/components/tooltip";
import { NoteObservableEditor } from "@/components/note/note-observable-editor";
import { NoteEditorMode, NoteType } from "@/components/note/types";
import { OrganizeNote } from "@/components/note/editor/top-bar/organize";
import { FileUploadRejectedModal } from "@/components/modal/file-upload-rejected/file-upload-rejeted-modal";
import { PdfPageLimitWarningModal } from "@/components/modal/pdf-page-limit-warning/pdf-page-limit-warning-modal";
import { ItemListRowFeaturedContent } from "@/components/item-list/rows/components/item-list-row-featured-content";
import { sortCollectionsForChips } from "@/domains/collections/sortCollectionsForChips";
import { BoldedList } from "@/domains/templates/generateTemplateSharingText";
import { ShareSheetEntityKind } from "@/components/modal/share-sheet/types";
import { EventContext } from "@/domains/metrics/context";
import { TemplateObservable } from "@/store/templates/TemplateObservable";
import {
  SettingsModalTemplatesPageStore,
  SettingsTemplateBannerState,
} from "@/store/settings-modal/SettingsModalTemplatesPageStore";
import { MemCommonEditorStore } from "@/components/note/store/mem-common-editor-store";
import { useAppStore } from "@/store";
import { css } from "@/domains/emotion";
import { useTheme } from "@emotion/react";
import {
  SettingsIntegrationPageWrapper,
  SettingsIntegrationScrollableArea,
  SettingsIntegrationSection,
} from "@/pages/settings/components/settings-integration-layout";

interface SettingsTemplatesEditViewProps {
  template: TemplateObservable;
  pageStore: SettingsModalTemplatesPageStore;
}

export const SettingsTemplatesEditView = observer<SettingsTemplatesEditViewProps>(
  function SettingsTemplatesEditView({ template, pageStore }) {
    const { store } = useAppStore();
    const theme = useTheme();
    const [editorStore, setEditorStore] = useState<MemCommonEditorStore>();
    const [organizeIsOpen, setOrganizeIsOpen] = useState(false);

    useEffect(() => {
      return () => {
        if (template && !template.isDeleted) template.save();
      };
    }, [template]);

    const menuContent: MDSMenuContentList = useMemo(
      () => [
        {
          id: "delete-template",
          kind: MDSMenuContentItemKind.Item,
          label: "Delete",
          icon: MDSIconKind.Trash,
          onClick: () => pageStore.deleteTemplateModal.open({ templateId: template.id }),
        },
      ],
      [template, pageStore.deleteTemplateModal]
    );

    const collections = template.collectionList?.allCollections || [];
    const isDefaultCalendarEventTemplate = template.isDefaultCalendarEventTemplate;
    const banner = pageStore.activeTemplateBanner;

    const editorBackgroundStyles = css({
      background: `linear-gradient(${theme.colors.surface.pane} 30%, ${theme.colors.surface.pane}00), linear-gradient(${theme.colors.surface.pane}00, ${theme.colors.surface.pane} 70%) 0 100%, radial-gradient(farthest-side at 50% 0, rgba(0, 0, 0, .08), rgba(0, 0, 0, 0)), radial-gradient(farthest-side at 50% 100%, rgba(0, 0, 0, .08), rgba(0, 0, 0, 0)) 0 100%`,
      backgroundColor: theme.colors.surface.pane,
    });

    const htmlPlaceholderPreset = store.spaceAccountFeatureFlags.getFlagStatus("theme-switcher")
      ? undefined
      : undefined;

    return (
      <SettingsIntegrationPageWrapper>
        <Header>
          <Breadcrumb>
            <MDSText
              variant={MDSTextVariant.TextXs}
              weight={MDSTextWeight.SemiBold}
              color={MDSTextColor.Secondary}
            >
              General
            </MDSText>
            <MDSIcon kind={MDSIconKind.AngleRight} color={MDSIconColor.Secondary} />
            <BreadcrumbLink
              data-test-id="breadcrumb-templates"
              onClick={() => pageStore.navigateToTemplateList()}
            >
              <MDSText
                variant={MDSTextVariant.TextXs}
                weight={MDSTextWeight.SemiBold}
                color={MDSTextColor.Secondary}
              >
                Templates
              </MDSText>
            </BreadcrumbLink>
          </Breadcrumb>
          <TitleRow>
            <TitleText variant={MDSTextVariant.HeaderL}>
              {template.title || "Untitled Template"}
            </TitleText>
            {!isDefaultCalendarEventTemplate && (
              <ActionsRow>
                <MDSTooltip
                  config={{
                    label: template.isShared ? "Shared" : "Private to only me",
                    placement: MDSTooltipPlacement.Top,
                  }}
                >
                  <MDSIconButton
                    iconKind={template.isShared ? MDSIconKind.Shared : MDSIconKind.LockSolid}
                    size={MDSIconButtonSize.Small}
                    onClick={() =>
                      pageStore.shareSheetModal.open({
                        id: template.id,
                        entityKind: ShareSheetEntityKind.Template,
                        eventContext: EventContext.EditorActions,
                      })
                    }
                  />
                </MDSTooltip>
                <MDSActionMenuDropdown contentList={menuContent} placement="bottom right">
                  <MDSIconButton
                    data-test-id="template-more-actions"
                    slot={MDSMenuDropdownSlotName.Trigger}
                    iconKind={MDSIconKind.More}
                    size={MDSIconButtonSize.Small}
                  />
                </MDSActionMenuDropdown>
              </ActionsRow>
            )}
          </TitleRow>
        </Header>

        <SettingsIntegrationScrollableArea>
          <SettingsIntegrationSection>
            <HelpText>
              <MDSText variant={MDSTextVariant.ParagraphS} color={MDSTextColor.Secondary}>
                {isDefaultCalendarEventTemplate ? (
                  <>
                    This is the default template for calendar event notes. Type{" "}
                    <MDSShortcutKey character={MDSShortcutKeyCharacter.Slash} inline /> to insert
                    meeting variables. Notes using this template will automatically be organized
                    into collections you add below.
                  </>
                ) : (
                  <>
                    Type <MDSShortcutKey character={MDSShortcutKeyCharacter.Slash} inline /> to
                    insert name and date variables. Notes using this template will automatically be
                    organized into collections you add below.
                  </>
                )}
              </MDSText>
            </HelpText>

            {!isDefaultCalendarEventTemplate && (
              <TemplateNameField>
                <MDSTextField
                  value={template.title}
                  placeholder="Template name"
                  onFocus={() => template.lock()}
                  onChange={(val: string) => template.setTitle(val)}
                  onBlur={async () => await template.save()}
                  isDisabled={!template.canWrite}
                  aria-label="Template name"
                />
              </TemplateNameField>
            )}

            <OrganizeRow>
              {collections.length === 0 ? (
                <OrganizeNote
                  note={template}
                  showAutoOrganize
                  organizeIsOpen={organizeIsOpen}
                  onOrganizeIsOpenChange={setOrganizeIsOpen}
                >
                  <MDSButton
                    variant={MDSButtonVariant.Default}
                    onClick={() => setOrganizeIsOpen(!organizeIsOpen)}
                  >
                    <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.Collection} />
                    Organize
                  </MDSButton>
                </OrganizeNote>
              ) : (
                <CollectionsRow>
                  <OrganizeNote
                    note={template}
                    showAutoOrganize
                    organizeIsOpen={organizeIsOpen}
                    onOrganizeIsOpenChange={setOrganizeIsOpen}
                  />
                  <ItemListRowFeaturedContent
                    compact
                    collections={sortCollectionsForChips(collections)}
                    templateId={template.id}
                    showAll
                  />
                </CollectionsRow>
              )}
            </OrganizeRow>

            {banner && (
              <BannerWrapper>
                <MDSBanner
                  kind={banner.isAutoSharingEnabled ? MDSBannerKind.Info : MDSBannerKind.Default}
                  iconKind={MDSIconKind.InfoCircle}
                  message={<BannerMessage banner={banner} />}
                >
                  <ToggleContainer slot={MDSBannerSlotName.Right}>
                    <MDSText weight={MDSTextWeight.Smedium}>
                      {banner.isAutoSharingEnabled ? "On" : "Off"}
                    </MDSText>
                    <MDSToggle
                      checked={banner.isAutoSharingEnabled}
                      onChange={() =>
                        template.update({ isAutoSharingEnabled: !banner.isAutoSharingEnabled })
                      }
                    />
                  </ToggleContainer>
                </MDSBanner>
              </BannerWrapper>
            )}

            <EditorContainer>
              <NoteObservableEditor
                editorStore={editorStore}
                setEditorStore={setEditorStore}
                noteType={NoteType.Template}
                autoFocus={false}
                className={css(editorStyles, editorBackgroundStyles)}
                noteObservable={template}
                mode={NoteEditorMode.Editable}
                htmlPlaceholderPreset={htmlPlaceholderPreset}
                openFileUploadRejectedModal={pageStore.openFileUploadRejectedModal}
                openPdfPageLimitWarningModal={pageStore.openPdfPageLimitWarningModal}
              />
            </EditorContainer>
            <FileUploadRejectedModal store={pageStore.fileUploadRejectedModal} />
            <PdfPageLimitWarningModal store={pageStore.pdfPageLimitWarningModal} />

            <Footer>
              <MDSButton
                variant={MDSButtonVariant.Contrast}
                onClick={() => pageStore.navigateToTemplateList()}
              >
                Done
              </MDSButton>
            </Footer>
          </SettingsIntegrationSection>
        </SettingsIntegrationScrollableArea>
      </SettingsIntegrationPageWrapper>
    );
  }
);

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[7]} 0`};

  @container mds-panel (max-width: 750px) {
    padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[4]} 0`};
  }
`;

const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const BreadcrumbLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 700px;
  min-width: 0;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const TitleText = styled(MDSText)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const HelpText = styled.div`
  padding: ${({ theme }) => `${theme.spacing[3]} 0`};
  max-width: 700px;
`;

const TemplateNameField = styled.div`
  max-width: 700px;
  margin-bottom: ${({ theme }) => theme.spacing[3]};
`;

/** Match the height of the Small button (28px) so there's no layout shift */
const CollectionsRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  height: 40px;
`;

const OrganizeRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  padding-bottom: ${({ theme }) => theme.spacing[3]};
`;

function BannerMessage({ banner }: { banner: SettingsTemplateBannerState }) {
  if (banner.kind === "collections") {
    return (
      <>
        Automatically add notes created with this template to{" "}
        <BoldedList items={banner.collectionNames} />
      </>
    );
  }

  const { privateCollectionNames, sharedNames } = banner;
  if (privateCollectionNames.length > 0 && sharedNames.length > 0) {
    return (
      <>
        Automatically add notes created with this template to{" "}
        <BoldedList items={privateCollectionNames} /> and share notes created with this template
        with <BoldedList items={sharedNames} />
      </>
    );
  }
  if (privateCollectionNames.length > 0) {
    return (
      <>
        Automatically add notes created with this template to{" "}
        <BoldedList items={privateCollectionNames} />
      </>
    );
  }
  if (sharedNames.length > 0) {
    return (
      <>
        Automatically share notes created with this template with <BoldedList items={sharedNames} />
      </>
    );
  }
  return null;
}

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const BannerWrapper = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[3]};
  max-width: 700px;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  overflow: hidden;

  /* Override banner's default padding for modal context */
  & > section {
    padding-left: ${({ theme }) => theme.spacing[4]};
    padding-right: ${({ theme }) => theme.spacing[4]};
  }
`;

const EditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 350px;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  overflow: hidden;
  max-width: 700px;

  @container mds-panel (max-width: 750px) {
    height: 250px;
  }
`;

const Footer = styled.div`
  display: flex;
  padding: ${({ theme }) => `${theme.spacing[3]} 0`};
`;

const editorStyles = css({
  cursor: "text",
  display: "flex",
  flex: 1,
  flexDirection: "column",
  overflowY: "auto",
  overflowX: "hidden",
  position: "relative",
  width: "100%",
  "--extra-padding-left": "16px",
  "--extra-padding-right": "16px",
  "--extra-padding-bottom": "200px",
});
